<template>
  <v-container fluid class="wave-picking">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="gradient-header" dark>
          <v-card-title class="text-h4 font-weight-bold">
            <v-icon large class="mr-3">mdi-waves</v-icon>
            Wave Picking
          </v-card-title>
          <v-card-subtitle class="text-h6">
            Optimización de picking por olas para procesamiento eficiente de múltiples órdenes
          </v-card-subtitle>
        </v-card>
      </v-col>
    </v-row>

    <!-- Estadísticas de Wave Picking -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card class="stats-card" color="primary" dark>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon large class="mr-3">mdi-package-variant-closed</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.pendingOrders }}</div>
                <div class="text-subtitle-1">Órdenes Pendientes</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="stats-card" color="success" dark>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon large class="mr-3">mdi-waves</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.activeWaves }}</div>
                <div class="text-subtitle-1">Olas Activas</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="stats-card" color="info" dark>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon large class="mr-3">mdi-speedometer</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.efficiency }}%</div>
                <div class="text-subtitle-1">Eficiencia</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card class="stats-card" color="warning" dark>
          <v-card-text>
            <div class="d-flex align-center">
              <v-icon large class="mr-3">mdi-timer</v-icon>
              <div>
                <div class="text-h4 font-weight-bold">{{ stats.avgTime }}min</div>
                <div class="text-subtitle-1">Tiempo Promedio</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs principales -->
    <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow>
      <v-tab>
        <v-icon class="mr-2">mdi-plus-circle</v-icon>
        Crear Ola
      </v-tab>
      <v-tab>
        <v-icon class="mr-2">mdi-chart-bubble</v-icon>
        Analizar Órdenes
      </v-tab>
      <v-tab>
        <v-icon class="mr-2">mdi-lightbulb</v-icon>
        Sugerencias
      </v-tab>
      <v-tab>
        <v-icon class="mr-2">mdi-cog</v-icon>
        Configuración
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Tab 1: Crear Ola -->
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-plus-circle</v-icon>
            Crear Nueva Ola de Picking
          </v-card-title>
          <v-card-text>
            <v-row>
              <!-- Selección de órdenes -->
              <v-col cols="12" md="8">
                <v-card outlined>
                  <v-card-title class="text-h6">Órdenes Disponibles</v-card-title>
                  <v-card-text>
                    <v-data-table
                      v-model="selectedOrders"
                      :headers="orderHeaders"
                      :items="availableOrders"
                      :loading="loadingOrders"
                      show-select
                      item-key="id"
                      class="elevation-1"
                      :search="orderSearch"
                    >
                      <template v-slot:top>
                        <v-text-field
                          v-model="orderSearch"
                          append-icon="mdi-magnify"
                          label="Buscar órdenes..."
                          single-line
                          hide-details
                          class="mb-4"
                        ></v-text-field>
                      </template>

                      <template v-slot:item.status="{ item }">
                        <v-chip
                          :color="getOrderStatusColor(item.status)"
                          dark
                          small
                        >
                          {{ item.status }}
                        </v-chip>
                      </template>

                      <template v-slot:item.priority="{ item }">
                        <v-chip
                          :color="getPriorityColor(item.priority)"
                          dark
                          small
                        >
                          {{ getPriorityText(item.priority) }}
                        </v-chip>
                      </template>

                      <template v-slot:item.total_items="{ item }">
                        {{ item.total_items || calculateOrderItems(item) }}
                      </template>

                      <template v-slot:item.required_date="{ item }">
                        {{ formatDate(item.required_date) }}
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Panel de configuración -->
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6">Configuración de Ola</v-card-title>
                  <v-card-text>
                    <v-alert
                      v-if="selectedOrders.length > 0"
                      type="info"
                      dense
                      class="mb-3"
                    >
                      {{ selectedOrders.length }} órdenes seleccionadas
                    </v-alert>

                    <v-text-field
                      v-model="waveConfig.name"
                      label="Nombre de la ola"
                      prepend-icon="mdi-tag"
                      hint="Opcional - se generará automáticamente"
                      persistent-hint
                    ></v-text-field>

                    <v-select
                      v-model="waveConfig.priority"
                      :items="priorities"
                      item-text="description"
                      item-value="value"
                      label="Prioridad de la ola"
                      prepend-icon="mdi-flag"
                    ></v-select>

                    <v-textarea
                      v-model="waveConfig.notes"
                      label="Notas"
                      prepend-icon="mdi-note-text"
                      rows="3"
                      auto-grow
                    ></v-textarea>

                    <v-divider class="my-4"></v-divider>

                    <v-btn
                      color="primary"
                      block
                      large
                      :loading="creatingWave"
                      :disabled="selectedOrders.length === 0"
                      @click="createWave"
                    >
                      <v-icon class="mr-2">mdi-rocket-launch</v-icon>
                      Crear Ola de Picking
                    </v-btn>

                    <v-btn
                      color="secondary"
                      block
                      large
                      class="mt-2"
                      :disabled="selectedOrders.length === 0"
                      @click="analyzeSelectedOrders"
                    >
                      <v-icon class="mr-2">mdi-chart-bubble</v-icon>
                      Analizar Primero
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 2: Analizar Órdenes -->
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-chart-bubble</v-icon>
            Análisis de Compatibilidad de Órdenes
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6">Seleccionar Órdenes para Análisis</v-card-title>
                  <v-card-text>
                    <v-autocomplete
                      v-model="analysisOrderIds"
                      :items="availableOrders"
                      item-text="order_number"
                      item-value="id"
                      label="Seleccionar órdenes"
                      multiple
                      chips
                      deletable-chips
                      prepend-icon="mdi-package-variant"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 3"
                          close
                          @click:close="removeFromAnalysis(item.id)"
                        >
                          {{ item.order_number }}
                        </v-chip>
                        <span
                          v-if="index === 3"
                          class="grey--text text-caption"
                        >
                          (+{{ analysisOrderIds.length - 3 }} más)
                        </span>
                      </template>
                    </v-autocomplete>

                    <v-btn
                      color="primary"
                      block
                      large
                      class="mt-3"
                      :loading="analyzingOrders"
                      :disabled="analysisOrderIds.length < 2"
                      @click="analyzeOrders"
                    >
                      <v-icon class="mr-2">mdi-play</v-icon>
                      Ejecutar Análisis
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined v-if="analysisResults">
                  <v-card-title class="text-h6">Resultados del Análisis</v-card-title>
                  <v-card-text>
                    <div class="mb-3">
                      <v-chip color="info" dark>
                        <v-icon left>mdi-information</v-icon>
                        {{ analysisResults.total_orders }} órdenes analizadas
                      </v-chip>
                    </div>

                    <!-- Recomendaciones -->
                    <div v-if="analysisResults.recommendations && analysisResults.recommendations.length > 0">
                      <h4 class="mb-2">Recomendaciones:</h4>
                      <v-expansion-panels class="mb-3">
                        <v-expansion-panel
                          v-for="(rec, index) in analysisResults.recommendations"
                          :key="index"
                        >
                          <v-expansion-panel-header>
                            <v-icon class="mr-2">mdi-lightbulb</v-icon>
                            {{ rec.description }}
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <div v-if="rec.type === 'location_overlap'">
                              <v-simple-table dense>
                                <template v-slot:default>
                                  <thead>
                                    <tr>
                                      <th>Órdenes</th>
                                      <th>Superposición</th>
                                      <th>Ubicaciones Compartidas</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="pair in rec.pairs" :key="pair.orders">
                                      <td>{{ pair.orders }}</td>
                                      <td>{{ pair.overlap_percentage.toFixed(1) }}%</td>
                                      <td>{{ pair.shared_locations }}</td>
                                    </tr>
                                  </tbody>
                                </template>
                              </v-simple-table>
                            </div>
                            <div v-else-if="rec.type === 'zone_grouping'">
                              <v-chip-group column>
                                <v-chip
                                  v-for="group in rec.groups"
                                  :key="group.zone_id"
                                  color="primary"
                                  dark
                                >
                                  Zona {{ group.zone_id }}: {{ group.order_count }} órdenes
                                </v-chip>
                              </v-chip-group>
                            </div>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>

                    <!-- Métricas de análisis -->
                    <div v-if="analysisResults.analysis">
                      <h4 class="mb-2">Métricas de Compatibilidad:</h4>
                      <v-simple-table dense>
                        <template v-slot:default>
                          <tbody>
                            <tr>
                              <td><strong>Superposiciones de ubicación:</strong></td>
                              <td>{{ Object.keys(analysisResults.analysis.location_overlap || {}).length }}</td>
                            </tr>
                            <tr>
                              <td><strong>Zonas involucradas:</strong></td>
                              <td>{{ Object.keys(analysisResults.analysis.zone_distribution || {}).length }}</td>
                            </tr>
                            <tr>
                              <td><strong>Grupos de prioridad:</strong></td>
                              <td>{{ Object.keys(analysisResults.analysis.priority_groups || {}).length }}</td>
                            </tr>
                          </tbody>
                        </template>
                      </v-simple-table>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 3: Sugerencias -->
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-lightbulb</v-icon>
            Sugerencias Automáticas de Olas
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-card outlined>
                  <v-card-title class="text-h6">Criterios de Sugerencia</v-card-title>
                  <v-card-text>
                    <v-select
                      v-model="suggestionCriteria.priority"
                      :items="[1, 2, 3, 4, 5]"
                      label="Prioridad mínima"
                      prepend-icon="mdi-flag"
                      hint="Solo órdenes con esta prioridad o mayor"
                      persistent-hint
                    ></v-select>

                    <v-select
                      v-model="suggestionCriteria.warehouse_id"
                      :items="warehouses"
                      item-text="name"
                      item-value="id"
                      label="Almacén"
                      prepend-icon="mdi-warehouse"
                      clearable
                    ></v-select>

                    <v-menu
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="suggestionCriteria.required_date_before"
                          label="Fecha límite"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="suggestionCriteria.required_date_before"
                        @input="dateMenu = false"
                      ></v-date-picker>
                    </v-menu>

                    <v-btn
                      color="primary"
                      block
                      large
                      class="mt-4"
                      :loading="loadingSuggestions"
                      @click="getSuggestions"
                    >
                      <v-icon class="mr-2">mdi-auto-fix</v-icon>
                      Generar Sugerencias
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="8">
                <v-card outlined v-if="suggestions">
                  <v-card-title class="text-h6">Olas Sugeridas</v-card-title>
                  <v-card-text>
                    <v-alert
                      v-if="suggestions.total_orders_analyzed === 0"
                      type="info"
                      dense
                    >
                      No hay órdenes que cumplan los criterios especificados
                    </v-alert>

                    <div v-else>
                      <div class="mb-3">
                        <v-chip color="success" dark class="mr-2">
                          <v-icon left>mdi-package-variant</v-icon>
                          {{ suggestions.total_orders_analyzed }} órdenes analizadas
                        </v-chip>
                        <v-chip color="info" dark>
                          <v-icon left>mdi-waves</v-icon>
                          {{ suggestions.suggested_wave_count }} olas sugeridas
                        </v-chip>
                      </div>

                      <!-- Métricas de eficiencia -->
                      <v-row class="mb-3">
                        <v-col cols="6">
                          <v-card color="primary" dark>
                            <v-card-text class="text-center">
                              <div class="text-h6">{{ suggestions.efficiency_metrics.average_orders_per_wave.toFixed(1) }}</div>
                              <div class="text-caption">Órdenes promedio por ola</div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                        <v-col cols="6">
                          <v-card color="success" dark>
                            <v-card-text class="text-center">
                              <div class="text-h6">{{ (suggestions.efficiency_metrics.location_utilization * 100).toFixed(1) }}%</div>
                              <div class="text-caption">Utilización de ubicaciones</div>
                            </v-card-text>
                          </v-card>
                        </v-col>
                      </v-row>

                      <!-- Lista de olas sugeridas -->
                      <v-expansion-panels>
                        <v-expansion-panel
                          v-for="wave in suggestions.suggested_waves"
                          :key="wave.wave_number"
                        >
                          <v-expansion-panel-header>
                            <div class="d-flex align-center">
                              <v-icon class="mr-2">mdi-waves</v-icon>
                              <span class="font-weight-bold">Ola {{ wave.wave_number }}</span>
                              <v-spacer></v-spacer>
                              <v-chip small color="primary" dark class="mr-2">
                                {{ wave.order_count }} órdenes
                              </v-chip>
                              <v-chip small color="success" dark>
                                {{ wave.total_items }} items
                              </v-chip>
                            </div>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <v-row>
                              <v-col cols="12" md="6">
                                <h4>Detalles:</h4>
                                <v-list dense>
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-list-item-title>Órdenes:</v-list-item-title>
                                      <v-list-item-subtitle>{{ wave.order_ids.join(', ') }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-list-item-title>Ubicaciones estimadas:</v-list-item-title>
                                      <v-list-item-subtitle>{{ wave.estimated_locations }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                  <v-list-item>
                                    <v-list-item-content>
                                      <v-list-item-title>Rango de prioridad:</v-list-item-title>
                                      <v-list-item-subtitle>{{ wave.priority_range.min }} - {{ wave.priority_range.max }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-col>
                              <v-col cols="12" md="6">
                                <h4>Clientes:</h4>
                                <v-chip-group column>
                                  <v-chip
                                    v-for="customer in wave.customers"
                                    :key="customer"
                                    small
                                    color="info"
                                    dark
                                  >
                                    {{ customer }}
                                  </v-chip>
                                </v-chip-group>
                                <v-btn
                                  color="primary"
                                  small
                                  class="mt-2"
                                  @click="createWaveFromSuggestion(wave)"
                                >
                                  <v-icon left>mdi-plus</v-icon>
                                  Crear Esta Ola
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Tab 4: Configuración -->
      <v-tab-item>
        <v-card class="mt-4">
          <v-card-title>
            <v-icon class="mr-2">mdi-cog</v-icon>
            Configuración del Sistema de Olas
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6">Límites de Ola</v-card-title>
                  <v-card-text>
                    <v-text-field
                      v-model.number="waveSettings.max_orders_per_wave"
                      type="number"
                      label="Máximo de órdenes por ola"
                      prepend-icon="mdi-package-variant"
                      min="1"
                      max="50"
                    ></v-text-field>

                    <v-text-field
                      v-model.number="waveSettings.max_locations_per_wave"
                      type="number"
                      label="Máximo de ubicaciones por ola"
                      prepend-icon="mdi-map-marker"
                      min="1"
                      max="100"
                    ></v-text-field>

                    <v-text-field
                      v-model.number="waveSettings.max_items_per_wave"
                      type="number"
                      label="Máximo de items por ola"
                      prepend-icon="mdi-cube"
                      min="1"
                      max="1000"
                    ></v-text-field>

                    <v-btn
                      color="primary"
                      :loading="updatingSettings"
                      @click="updateWaveSettings"
                    >
                      <v-icon class="mr-2">mdi-content-save</v-icon>
                      Guardar Configuración
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card outlined>
                  <v-card-title class="text-h6">Información del Sistema</v-card-title>
                  <v-card-text>
                    <v-list>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Configuración actual:</v-list-item-title>
                          <v-list-item-subtitle>
                            Máx {{ waveSettings.max_orders_per_wave }} órdenes, 
                            {{ waveSettings.max_locations_per_wave }} ubicaciones, 
                            {{ waveSettings.max_items_per_wave }} items por ola
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Algoritmo de optimización:</v-list-item-title>
                          <v-list-item-subtitle>Análisis de compatibilidad basado en ubicaciones y zonas</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title>Criterios de agrupación:</v-list-item-title>
                          <v-list-item-subtitle>Superposición de ubicaciones, distribución por zonas, prioridades</v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>

                    <v-alert type="info" dense class="mt-3">
                      <strong>Tip:</strong> Ajusta los límites según la capacidad de tu equipo y la complejidad del almacén.
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialog para resultado de creación de ola -->
    <v-dialog v-model="waveResultDialog" max-width="800px">
      <v-card v-if="waveResult">
        <v-card-title>
          <v-icon class="mr-2" color="success">mdi-check-circle</v-icon>
          Ola Creada Exitosamente
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>ID de Ola:</v-list-item-title>
                    <v-list-item-subtitle class="text-h6">{{ waveResult.wave_id }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Órdenes procesadas:</v-list-item-title>
                    <v-list-item-subtitle>{{ waveResult.total_orders }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Grupos de ola:</v-list-item-title>
                    <v-list-item-subtitle>{{ waveResult.wave_groups }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>Tiempo total estimado:</v-list-item-title>
                    <v-list-item-subtitle>{{ waveResult.estimated_total_time }} minutos</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" md="6">
              <h4>Tareas Creadas:</h4>
              <v-list dense>
                <v-list-item
                  v-for="(task, index) in waveResult.tasks"
                  :key="index"
                >
                  <v-list-item-content>
                    <v-list-item-title>Tarea {{ index + 1 }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ task.total_items }} items, {{ task.estimated_time }} min
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="waveResultDialog = false">
            Cerrar
          </v-btn>
          <v-btn color="primary" @click="viewWaveDetails">
            <v-icon class="mr-2">mdi-eye</v-icon>
            Ver Detalles
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import api from '@/api'

export default {
  name: 'WavePicking',
  data() {
    return {
      activeTab: 0,
      
      // Estadísticas
      stats: {
        pendingOrders: 0,
        activeWaves: 0,
        efficiency: 0,
        avgTime: 0
      },
      
      // Datos maestros
      availableOrders: [],
      warehouses: [],
      priorities: [],
      
      // Estados de carga
      loadingOrders: false,
      creatingWave: false,
      analyzingOrders: false,
      loadingSuggestions: false,
      updatingSettings: false,
      
      // Crear ola
      selectedOrders: [],
      orderSearch: '',
      orderHeaders: [
        { text: 'Número', value: 'order_number' },
        { text: 'Cliente', value: 'customer_name' },
        { text: 'Estado', value: 'status' },
        { text: 'Prioridad', value: 'priority' },
        { text: 'Items', value: 'total_items' },
        { text: 'Fecha Requerida', value: 'required_date' }
      ],
      waveConfig: {
        name: '',
        priority: 2,
        notes: ''
      },
      
      // Análisis de órdenes
      analysisOrderIds: [],
      analysisResults: null,
      
      // Sugerencias
      suggestionCriteria: {
        priority: 1,
        warehouse_id: null,
        required_date_before: null
      },
      suggestions: null,
      dateMenu: false,
      
      // Configuración
      waveSettings: {
        max_orders_per_wave: 10,
        max_locations_per_wave: 20,
        max_items_per_wave: 50
      },
      
      // Dialogs
      waveResultDialog: false,
      waveResult: null,
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      }
    }
  },
  
  async created() {
    await this.loadInitialData()
  },
  
  methods: {
    async loadInitialData() {
      try {
        await Promise.all([
          this.loadOrders(),
          this.loadWarehouses(),
          this.loadPriorities(),
          this.loadWaveSettings()
        ])
        this.updateStats()
      } catch (error) {
        this.showError('Error al cargar datos iniciales')
      }
    },
    
    async loadOrders() {
      this.loadingOrders = true
      try {
        const response = await api.getOrders({ status: 'pending,assigned' })
        this.availableOrders = response.data.map(order => ({
          ...order,
          customer_name: order.customer?.name || 'Sin cliente',
          total_items: this.calculateOrderItems(order)
        }))
      } catch (error) {
        this.showError('Error al cargar órdenes')
      } finally {
        this.loadingOrders = false
      }
    },
    
    async loadWarehouses() {
      try {
        const response = await api.getWarehouses()
        this.warehouses = response.data
      } catch (error) {
        this.showError('Error al cargar almacenes')
      }
    },
    
    async loadPriorities() {
      try {
        const response = await api.getPickingPriorities()
        this.priorities = response.data.priorities
      } catch (error) {
        this.showError('Error al cargar prioridades')
      }
    },
    
    async loadWaveSettings() {
      try {
        const response = await api.getWaveSettings()
        this.waveSettings = response.data
      } catch (error) {
        this.showError('Error al cargar configuración')
      }
    },
    
    updateStats() {
      this.stats = {
        pendingOrders: this.availableOrders.length,
        activeWaves: 3, // Simulado
        efficiency: 92, // Simulado
        avgTime: 45 // Simulado
      }
    },
    
    calculateOrderItems(order) {
      if (!order.order_lines) return 0
      return order.order_lines.reduce((sum, line) => sum + line.quantity, 0)
    },
    
    async createWave() {
      if (this.selectedOrders.length === 0) return
      
      this.creatingWave = true
      try {
        const waveData = {
          order_ids: this.selectedOrders.map(order => order.id),
          name: this.waveConfig.name,
          priority: this.waveConfig.priority,
          notes: this.waveConfig.notes
        }
        
        const response = await api.createPickingWave(waveData)
        this.waveResult = response.data
        this.waveResultDialog = true
        
        // Limpiar selección
        this.selectedOrders = []
        this.waveConfig = { name: '', priority: 2, notes: '' }
        
        await this.loadOrders()
        
      } catch (error) {
        this.showError('Error al crear ola de picking')
      } finally {
        this.creatingWave = false
      }
    },
    
    analyzeSelectedOrders() {
      if (this.selectedOrders.length === 0) return
      
      this.analysisOrderIds = this.selectedOrders.map(order => order.id)
      this.activeTab = 1 // Cambiar a tab de análisis
      this.analyzeOrders()
    },
    
    async analyzeOrders() {
      if (this.analysisOrderIds.length < 2) return
      
      this.analyzingOrders = true
      try {
        const response = await api.analyzeOrdersForWave({
          order_ids: this.analysisOrderIds
        })
        this.analysisResults = response.data
        
      } catch (error) {
        this.showError('Error al analizar órdenes')
      } finally {
        this.analyzingOrders = false
      }
    },
    
    removeFromAnalysis(orderId) {
      const index = this.analysisOrderIds.indexOf(orderId)
      if (index > -1) {
        this.analysisOrderIds.splice(index, 1)
      }
    },
    
    async getSuggestions() {
      this.loadingSuggestions = true
      try {
        const criteria = { ...this.suggestionCriteria }
        
        // Formatear fecha si existe
        if (criteria.required_date_before) {
          criteria.required_date_before = new Date(criteria.required_date_before).toISOString()
        }
        
        const response = await api.suggestWaveConfiguration({ criteria })
        this.suggestions = response.data
        
      } catch (error) {
        this.showError('Error al generar sugerencias')
      } finally {
        this.loadingSuggestions = false
      }
    },
    
    async createWaveFromSuggestion(wave) {
      try {
        const waveData = {
          order_ids: wave.order_ids,
          name: `Ola Sugerida ${wave.wave_number}`,
          priority: wave.priority_range.max,
          notes: `Ola generada automáticamente con ${wave.order_count} órdenes`
        }
        
        const response = await api.createPickingWave(waveData)
        this.waveResult = response.data
        this.waveResultDialog = true
        
        await this.loadOrders()
        
      } catch (error) {
        this.showError('Error al crear ola desde sugerencia')
      }
    },
    
    async updateWaveSettings() {
      this.updatingSettings = true
      try {
        await api.updateWaveSettings(this.waveSettings)
        this.showSuccess('Configuración actualizada exitosamente')
        
      } catch (error) {
        this.showError('Error al actualizar configuración')
      } finally {
        this.updatingSettings = false
      }
    },
    
    viewWaveDetails() {
      // Aquí implementaríamos la navegación a detalles de ola
      this.waveResultDialog = false
      this.showSuccess('Funcionalidad de detalles en desarrollo')
    },
    
    // Métodos de utilidad
    getOrderStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'assigned': 'blue',
        'in_progress': 'purple',
        'completed': 'green',
        'cancelled': 'red'
      }
      return colors[status] || 'grey'
    },
    
    getPriorityColor(priority) {
      const colors = {
        1: 'grey',
        2: 'blue',
        3: 'orange',
        4: 'red',
        5: 'purple'
      }
      return colors[priority] || 'grey'
    },
    
    getPriorityText(priority) {
      const texts = {
        1: 'Baja',
        2: 'Normal',
        3: 'Alta',
        4: 'Urgente',
        5: 'Crítica'
      }
      return texts[priority] || 'Normal'
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },
    
    showSuccess(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'success',
        timeout: 4000
      }
    },
    
    showError(message) {
      this.snackbar = {
        show: true,
        message,
        color: 'error',
        timeout: 6000
      }
    }
  }
}
</script>

<style scoped>
.wave-picking {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.gradient-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stats-card {
  transition: transform 0.2s;
}

.stats-card:hover {
  transform: translateY(-2px);
}
</style> 